<script setup lang="ts">
/**
 * Module: web/src/components/entry/ReasonGroupSelect.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.1 (P8 polish: selected-count pill -> shared .chip system)
 * Date: 2026-07-08
 *
 * Grouped specific-reasons multi-select (doc 05 sect 3.4 #15 + sect 4.3,
 * Decision D3). Renders one checkbox section per bootstrap reason group.
 *
 * Sentinel mutual-exclusion (sect 4.3 / O5) -- BOTH directions:
 *   - checking any REAL reason auto-unchecks every sentinel ("Not specified")
 *   - checking a SENTINEL clears every real reason (selection becomes [sentinel])
 *
 * >= 1 reason is required (server-enforced; the indicator here is UX only).
 */
import { computed } from 'vue';
import type { MoBootstrapReason } from '@/types/api';

const props = defineProps<{
  /** Bootstrap reasons, keyed by group name (order preserved). */
  groups: Record<string, MoBootstrapReason[]>;
  /** Selected reason ids. */
  modelValue: number[];
  disabled?: boolean;
  /** Validation message (client or server 422) -- renders the error state. */
  error?: string | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: number[]): void;
}>();

/** Friendly labels for the known group keys; fallback = title-cased key. */
const GROUP_LABELS: Record<string, string> = {
  price: 'Price',
  eta: 'ETA / Response',
  coverage: 'Coverage / Capacity',
  customer: 'Customer',
  other: 'Other',
  sentinel: 'Not specified',
};

function groupLabel(key: string): string {
  return (
    GROUP_LABELS[key] ??
    key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

const groupEntries = computed(() => Object.entries(props.groups));
const allReasons = computed(() => Object.values(props.groups).flat());
const selectedCount = computed(() => props.modelValue.length);

function isChecked(id: number): boolean {
  return props.modelValue.includes(id);
}

function toggle(reason: MoBootstrapReason, event?: Event) {
  if (props.disabled) return;
  let next: number[];
  if (isChecked(reason.id)) {
    next = props.modelValue.filter((id) => id !== reason.id);
  } else if (reason.is_sentinel) {
    // Sentinel wins alone: picking "Not specified" clears every real reason.
    next = [reason.id];
  } else {
    // Real reason: drop any selected sentinel, then add (sect 4.3 auto-clear).
    const sentinelIds = new Set(allReasons.value.filter((r) => r.is_sentinel).map((r) => r.id));
    next = [...props.modelValue.filter((id) => !sentinelIds.has(id)), reason.id];
  }
  // Force the clicked input to the model outcome: the native click already
  // flipped its DOM state, and Vue only patches when the BOUND value changes.
  if (event?.target instanceof HTMLInputElement) {
    event.target.checked = next.includes(reason.id);
  }
  emit('update:modelValue', next);
}
</script>

<template>
  <div>
    <div class="flex items-center justify-between gap-2 mb-2">
      <span class="text-sm text-fg-muted">
        Select every reason that applies
        <span class="font-semibold" :class="selectedCount > 0 ? 'text-accent-good' : 'text-accent-warm'">
          (≥ 1 required)
        </span>
      </span>
      <span
        v-if="selectedCount > 0"
        class="chip chip-accent"
      >{{ selectedCount }} selected</span>
    </div>

    <div v-if="error" class="alert-error mb-3" role="alert">{{ error }}</div>

    <p v-if="groupEntries.length === 0" class="text-sm text-fg-muted py-2">
      Reason list unavailable — reload the form data to select reasons.
    </p>

    <fieldset
      v-for="[key, reasons] in groupEntries"
      :key="key"
      class="mb-3 last:mb-0"
      :disabled="disabled"
    >
      <legend class="text-xs uppercase tracking-wide text-fg-muted mb-1.5">{{ groupLabel(key) }}</legend>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label
          v-for="reason in reasons"
          :key="reason.id"
          class="flex items-center gap-3 min-h-[48px] px-3 py-2 rounded-lg border cursor-pointer
            transition-colors select-none"
          :class="isChecked(reason.id)
            ? 'bg-accent/10 border-accent text-fg'
            : 'bg-input border-border-c text-fg hover:bg-card'"
        >
          <input
            type="checkbox"
            class="w-5 h-5 rounded accent-[var(--accent)] flex-shrink-0"
            :checked="isChecked(reason.id)"
            :disabled="disabled"
            @change="toggle(reason, $event)"
          />
          <span class="text-base leading-snug">
            {{ reason.code }}
            <span
              v-if="reason.is_sentinel"
              class="ml-1 text-[10px] uppercase tracking-wide text-fg-muted"
            >(excludes others)</span>
          </span>
        </label>
      </div>
    </fieldset>
  </div>
</template>
